import { interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';

// Types
import { AnimationPreset } from '@/types';

// Constants
import { ANIMATION_SPRING_CONFIGS, ANIMATION_DEFAULTS } from '@/constants';

/**
 * Direction from which the element slides in
 */
export enum SlideDirection {
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right',
}

/**
 * Configuration for slide-in animation
 */
export interface SlideInAnimationConfig {
  /** Direction from which the element slides in (default: 'bottom') */
  direction?: SlideDirection;
  /** Distance to travel in pixels (default: 200) */
  distance?: number;
  /** Starting frame for animation (default: 0) */
  startFrame?: number;
  /** Duration of the slide animation in frames (uses spring default if not specified) */
  durationInFrames?: number;
  /** Duration for opacity transition in frames (default: 10) */
  opacityDuration?: number;
  /** Spring configuration to use */
  springConfig?: AnimationPreset;
}

/**
 * Return type for slide-in animation
 */
export interface SlideInAnimationState {
  /** The translate value to apply (in pixels), ready to use with translateX or translateY */
  translateValue: number;
  /** Opacity value from 0 to 1 */
  opacity: number;
  /** The CSS transform string ready to apply */
  transform: string;
}

/**
 * Hook for creating a slide-in animation from a specified direction.
 * Returns translateValue, opacity, and a ready-to-use transform string.
 *
 * @param config Animation configuration including direction and distance
 * @returns Animation state with translateValue, opacity, and transform
 */
export const useSlideInAnimation = (
  config: SlideInAnimationConfig = {},
): SlideInAnimationState => {
  const {
    direction = SlideDirection.Bottom,
    distance = 200,
    startFrame = ANIMATION_DEFAULTS.startFrame,
    durationInFrames,
    opacityDuration = ANIMATION_DEFAULTS.stagger,
    springConfig = AnimationPreset.DEFAULT,
  } = config;

  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate progress (0 to 1)
  // If durationInFrames is specified, use linear interpolation for full-duration animation
  // Otherwise, use spring for natural physics-based animation
  const progress = durationInFrames
    ? interpolate(frame, [startFrame, startFrame + durationInFrames], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      })
    : spring({
        frame: frame - startFrame,
        fps,
        config: ANIMATION_SPRING_CONFIGS[springConfig],
      });

  // Calculate the remaining distance (starts at full distance, ends at 0)
  const remainingDistance = (1 - progress) * distance;

  // Determine translate value based on direction
  let translateValue: number;
  let transform: string;

  switch (direction) {
    case SlideDirection.Top:
      // Slides in from top (negative Y direction)
      translateValue = -remainingDistance;
      transform = `translateY(${translateValue}px)`;
      break;
    case SlideDirection.Bottom:
      // Slides in from bottom (positive Y direction)
      translateValue = remainingDistance;
      transform = `translateY(${translateValue}px)`;
      break;
    case SlideDirection.Left:
      // Slides in from left (negative X direction)
      translateValue = -remainingDistance;
      transform = `translateX(${translateValue}px)`;
      break;
    case SlideDirection.Right:
      // Slides in from right (positive X direction)
      translateValue = remainingDistance;
      transform = `translateX(${translateValue}px)`;
      break;
  }

  const opacity = interpolate(
    frame,
    [startFrame, startFrame + opacityDuration],
    [0, 1],
    {
      extrapolateRight: 'clamp',
    },
  );

  return { translateValue, opacity, transform };
};
